/**
 * FeedPage — the personalized activity feed (DEV-1330, epic DEV-1329).
 *
 * Renders the fan-in-on-read feed: a recency-ranked, category-capped union of
 * the user's followed/belonged sources. Transparent type-filters let the user
 * (or a teacher/parent) dial the feed to all-substance; the choice persists.
 * Ranking is never engagement-optimized (locked, DEV-1330).
 *
 * Mounted at /feed.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { fetchFeed, FeedItem, FeedResponse } from '../../api/feed';
import { deletePost } from '../../api/newsApi';
import { FeedComposer } from '../../Components/Feed/FeedComposer';
import { FeedCard, type FeedCardModel } from '../../Components/Feed/FeedCard';
import VoiceMomentRibbon from '../../Components/VoiceMomentRibbon/VoiceMomentRibbon';
import FellowMembersScroller from '../../Components/FellowMemberCard/FellowMembersScroller';
import { fetchFellowMembers, dismissDiscoveryCandidate, type FellowMemberCandidate } from '../../api/discovery';
import { mediaSrc } from '../../api/mediaUrl';
import { withYouTubeAutoplay } from '../../utils/embedAutoplay';
import { useAuth } from '../../auth/AuthContext';
import { FeedActionBar } from '../../Components/Comments/FeedActionBar';
// Voice-ladder gate (Skip 2026-07-07): the main feed is READ/discovery for
// Explorers (it's how they find things to follow); the composer is voice
// and renders Contributor+. Server enforces too (NewsController.Share).
import { useApiOnboarding } from '../../api/useApiOnboarding';
import styles from './FeedPage.module.css';

// DEV-720 — feed items whose underlying entity is a registered, commentable
// anchor. Comments attach to that entity's thread (not a feed-event store).
const COMMENTABLE_FEED_OBJECTS = new Set(['project', 'mini_project', 'chronicle', 'news_item']);
function feedAnchor(item: FeedItem): { anchorType: string; anchorId: string } | null {
  if (item.objectType && item.objectGuid && COMMENTABLE_FEED_OBJECTS.has(item.objectType)) {
    return { anchorType: item.objectType, anchorId: item.objectGuid };
  }
  return null;
}

interface Props {
  currentUserGuid: string;
}

const PAGE_SIZE = 25;

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffSec = Math.round((then - Date.now()) / 1000);
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  const abs = Math.abs(diffSec);
  if (abs < 60) return rtf.format(Math.round(diffSec), 'second');
  if (abs < 3600) return rtf.format(Math.round(diffSec / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(diffSec / 3600), 'hour');
  if (abs < 2592000) return rtf.format(Math.round(diffSec / 86400), 'day');
  if (abs < 31536000) return rtf.format(Math.round(diffSec / 2592000), 'month');
  return rtf.format(Math.round(diffSec / 31536000), 'year');
}

const FeedPage: React.FC<Props> = ({ currentUserGuid }) => {
  const { t } = useAppTranslation('pages');
  const navigate = useNavigate();
  const { user } = useAuth();
  // Open-feed comments are 18+ only (age gate is the audience control). Minors
  // still see posts + reactions. Server enforces too (CommentAuth).
  const isAdult = !!user?.isAdult;

  // Voice-ladder: Explorers read the main feed (discovery — their runway to
  // Contributor is literally "follow 3"); posting here is platform-wide
  // voice and unlocks at Contributor. Null archetype (legacy) = permissive.
  const { archetype: viewerArchetype } = useApiOnboarding(currentUserGuid || null);
  const canComposeMainFeed = viewerArchetype !== 'explorer';

  const [items, setItems] = useState<FeedItem[]>([]);
  // DEV-1441 — first-post Voice moment ribbon (set on firstPost share response).
  const [voiceMoment, setVoiceMoment] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Feed Member Discovery (Slice C). A "Fellow members of {Org}" card showing
  // several members in a horizontal scroller, injected client-side at a
  // cadence (not server-injected into the feed query). Gated to adult viewers
  // — the whole feature is the adult pilot, and contact-request initiation
  // surfaces are a minor-safety watch-item.
  const [discovery, setDiscovery] = useState<FellowMemberCandidate[]>([]);
  useEffect(() => {
    if (!currentUserGuid || !isAdult) return;
    let cancelled = false;
    fetchFellowMembers(10)
      .then((rows) => { if (!cancelled) setDiscovery(rows); })
      .catch(() => { /* discovery is best-effort; never blocks the feed */ });
    return () => { cancelled = true; };
  }, [currentUserGuid, isAdult]);

  const handleDiscoveryDismiss = useCallback((targetGuid: string, kind: 'snooze' | 'ignore') => {
    // Remove just that member from the row; server records the snooze/ignore.
    setDiscovery((prev) => prev.filter((c) => c.targetGuid !== targetGuid));
    dismissDiscoveryCandidate(targetGuid, kind).catch(() => { /* best-effort */ });
  }, []);

  const applyResponse = useCallback((res: FeedResponse, append: boolean) => {
    setItems((prev) => (append ? [...prev, ...res.items] : res.items));
    setHasMore(res.hasMore);
  }, []);

  const load = useCallback(async () => {
    if (!currentUserGuid) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchFeed(currentUserGuid, {
        limit: PAGE_SIZE,
        offset: 0,
      });
      setOffset(res.items.length);
      applyResponse(res, false);
    } catch (e: any) {
      setError(e?.message ?? t('feed.loadError'));
    } finally {
      setLoading(false);
    }
  }, [currentUserGuid, applyResponse, t]);

  const loadMore = useCallback(async () => {
    if (!currentUserGuid || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await fetchFeed(currentUserGuid, {
        limit: PAGE_SIZE,
        offset,
      });
      setOffset((o) => o + res.items.length);
      applyResponse(res, true);
    } catch {
      /* keep what we have */
    } finally {
      setLoadingMore(false);
    }
  }, [currentUserGuid, offset, applyResponse, loadingMore]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = useCallback(async (item: FeedItem): Promise<boolean> => {
    if (!item.postGuid) return false;
    if (!window.confirm(t('feed.deleteConfirm'))) return false;
    try {
      await deletePost(item.postGuid);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      return true;
    } catch {
      window.alert(t('feed.deleteError'));
      return false;
    }
  }, [t]);

  const openItem = useCallback((item: FeedItem) => {
    if (!item.href) return;
    if (item.href.startsWith('/')) navigate(item.href);
    else window.open(item.href, '_blank', 'noopener,noreferrer');
  }, [navigate]);

  const openAuthor = useCallback((item: FeedItem) => {
    if (item.authorHref && item.authorHref.startsWith('/')) navigate(item.authorHref);
  }, [navigate]);

  const openSource = useCallback((item: FeedItem) => {
    if (!item.sourceEntityHref || !item.sourceEntityHref.startsWith('/')) return;
    // Community sources deep-link to the originating surface: News or Feed tab.
    let href = item.sourceEntityHref;
    if (item.sourceEntityType === 'community') {
      href += item.sourceEntitySurface === 'news' ? '#news' : '#feed';
    }
    navigate(href);
  }, [navigate]);

  // Display label for a source: a community reads as "{name} News" or
  // "{name} Feed" depending on origin; other sources use their plain name.
  const sourceLabel = useCallback(
    (item: FeedItem) => {
      if (item.sourceEntityType !== 'community' || !item.sourceEntityName) {
        return item.sourceEntityName || '';
      }
      return item.sourceEntitySurface === 'news'
        ? t('feed.communityNewsLabel', { name: item.sourceEntityName })
        : t('feed.communitySourceLabel', { name: item.sourceEntityName });
    },
    [t],
  );

  // Funnel-CTA copy: reads better per surface than a generic "See more from".
  const ctaLabel = useCallback(
    (item: FeedItem) => {
      const name = item.sourceEntityName || '';
      if (item.sourceEntityType === 'community') {
        return item.sourceEntitySurface === 'news'
          ? t('feed.ctaReadNews', { name })   // "Read more news from {name}"
          : t('feed.ctaGoToFeed', { name });  // "Go to the {name} feed"
      }
      return t('feed.seeMoreFrom', { name }); // chronicle / project sources
    },
    [t],
  );

  // Adapter: aggregated FeedItem → the shared FeedCard model.
  const toCardModel = useCallback((item: FeedItem): FeedCardModel => {
    const anchor = feedAnchor(item);
    const ext = item.href && !item.href.startsWith('/') ? item.href : null;
    const hasSource = !!(item.sourceEntityName && item.sourceEntityHref);
    return {
      id: item.id,
      pillar: item.pillar,
      authorName: item.authorName || item.sourceName,
      authorAvatarUrl: item.authorAvatarUrl,
      authorHref: item.authorHref,
      postedInName: item.authorType === 'user' && hasSource ? sourceLabel(item) : null,
      postedInHref: item.sourceEntityHref,
      dateText: relativeTime(item.timestamp),
      badgeText: t(`feed.category.${item.category}`),
      embedHtml: item.embedHtml ? withYouTubeAutoplay(item.embedHtml) : null,
      embedType: item.embedType,
      imageSrc: item.imageUrl ? (mediaSrc(item.imageUrl) ?? null) : null,
      theaterImageUrl: item.imageUrl,
      title: item.title,
      body: item.body,
      linkUrl: ext,
      anchorType: anchor?.anchorType ?? null,
      anchorId: anchor?.anchorId ?? null,
      onOpen: item.href ? () => openItem(item) : undefined,
      onAuthor: item.authorHref ? () => openAuthor(item) : undefined,
      ctaLabel: hasSource ? ctaLabel(item) : null,
      onCta: hasSource ? () => openSource(item) : undefined,
      menuActions: item.canDelete
        ? [{ key: 'delete', label: t('feed.deletePost'), danger: true, onSelect: () => handleDelete(item) }]
        : [],
    };
  }, [t, openItem, openAuthor, openSource, sourceLabel, ctaLabel, handleDelete]);

  return (
    <div className={styles.page}>
      {canComposeMainFeed ? (
        <FeedComposer
          onShared={(firstPost) => {
            load();
            // DEV-1441 Voice moment — first-ever share, once per lifetime.
            if (firstPost) setVoiceMoment(true);
          }}
        />
      ) : (
        <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>
          {t('feed.postingLockedExplorer')}
        </p>
      )}

      {/* DEV-1441 — one-time first-post Voice ribbon (variant B). */}
      <VoiceMomentRibbon open={voiceMoment} onDismiss={() => setVoiceMoment(false)} />

      {loading ? (
        <div className={styles.state}>{t('feed.loading')}</div>
      ) : error ? (
        <div className={styles.state}>
          <p>{error}</p>
          <button type="button" className={styles.retry} onClick={load}>{t('feed.retry')}</button>
        </div>
      ) : items.length === 0 ? (
        <div className={styles.empty}>
          <h2 className={styles.emptyTitle}>{t('feed.empty.title')}</h2>
          <p className={styles.emptyBody}>{t('feed.empty.body')}</p>
          <button type="button" className={styles.emptyCta} onClick={() => navigate('/communities')}>
            {t('feed.empty.cta')}
          </button>
        </div>
      ) : (
        <>
          <div className={styles.list}>
            {(() => {
              const nodes: React.ReactNode[] = items.map((item) => (
                <FeedCard key={item.id} model={toCardModel(item)} enableComments={isAdult} />
              ));
              // Inject the discovery card at a cadence (after the 3rd item, or
              // at the end for short feeds) — not on every refresh, not every
              // item. One card per load, showing several members in a
              // horizontal scroller.
              if (discovery.length > 0) {
                const pos = Math.min(3, nodes.length);
                nodes.splice(pos, 0, (
                  <div key="discovery:fellow-members" className={styles.discoveryCard}>
                    <div className={styles.discoveryHeader}>
                      {t('feed.discoveryHeader', { org: discovery[0].orgName })}
                    </div>
                    <FellowMembersScroller
                      candidates={discovery}
                      onDismiss={handleDiscoveryDismiss}
                    />
                  </div>
                ));
              }
              return nodes;
            })()}
          </div>

          {hasMore && (
            <div className={styles.moreRow}>
              <button type="button" className={styles.loadMore} onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? t('feed.loading') : t('feed.loadMore')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FeedPage;
